import { flash, Label, SubmitButton, Switch } from "@xola/ui-kit";
import { getDemographicCatalog, SecondaryButton } from "common";
import dayjs from "dayjs";
import { produce } from "immer";
import { compact, uniq } from "lodash";
import React from "react";
import { useMutation } from "react-query";
import {
    AdjustmentOperation,
    AdjustmentType,
    CatalogItem,
    DemographicPrice,
    Experience,
    LineItemCode,
    LineItemDestination,
    LineItemTemplate,
    LineItemTemplateObject,
    LineItemType,
    LineItemVisibility,
    PartnerDemographicPrice,
    PartnerDemographicTerm,
    PreferenceContext,
    PreferenceKey,
    PurchaseRule,
    PurchaseRuleActionObject,
    PurchaseRuleLink,
    PurchaseRuleShortcutType,
    SellerPartnerPreference,
    TagId,
    TermsTemplate,
    TermsTemplateFee,
    TermsType,
    XolaPartner,
    XolaPartnerConnectionType,
    XolaPartnerStatus,
} from "types";
import { ContentLayout } from "../../Layout/ContentLayout";
import { api } from "@/api/api";
import { DemographicTermTable } from "@/components/Agent/TermsTemplate/DemographicTermTable";
import { ProductImage } from "@/components/Common/ProductImage";
import { XolaFormik } from "@/components/Common/XolaFormik";
import { Loader } from "@/components/Loader";
import { useExperiences } from "@/hooks/resources/useProduct";
import { useSeller } from "@/hooks/useAccount";
import { useAutoFetchCollection } from "@/hooks/useAutoFetchCollection";
import { useCollection } from "@/hooks/useCollection";
import { useInvalidate } from "@/hooks/useInvalidation";
import { generateProductTermExecptions } from "@/models/ProductTermException";
import { getPurchaseRulesByTag } from "@/models/PurchaseRule";
import { getPurchaseRulesFromPurchaseRuleLink } from "@/models/PurchaseRuleLink";
import {
    generatePartnerDemographicTerms,
    getAlgorithm,
    getDemographicSelection,
    getIncludeCustomLineItems,
    getIncludesAddOns,
    getInitialPartnerDemographicPrices,
    getProductSelection,
    getProductSelectionFromPurchaseRules,
    getScope,
    getTermsType,
} from "@/models/TermsTemplatePurchaseRule";

interface PartnerTermsFormValues {
    partnerResells: boolean;
    selectedExperiences: ID[];
}
interface PartnerTermsPageProps {
    partner: XolaPartner;
}

export const PartnerTermsPage = ({ partner }: PartnerTermsPageProps) => {
    const seller = useSeller();
    const { data: partnerXolaPartners, isLoading: isLoadingPartnerSeller } = useCollection<XolaPartner>("partners", {
        partnerSeller: seller.id,
        seller: partner?.partnerSeller.id,
        status: XolaPartnerStatus.ACCEPTED,
        expand: "purchaseRule,partnerSeller",
    });

    const partnerXolaPartner: XolaPartner = partnerXolaPartners?.[0] ?? null;

    if (isLoadingPartnerSeller) {
        return <Loader />;
    }

    return (
        <div>
            {partnerXolaPartner ? <PartnerTerms partner={partner} partnerXolaPartner={partnerXolaPartner} /> : null}
        </div>
    );
};

interface PartnerTermsProps {
    partner: XolaPartner;
    partnerXolaPartner: XolaPartner;
}

export const PartnerTerms = ({ partner, partnerXolaPartner }: PartnerTermsProps) => {
    const invalidate = useInvalidate();
    const seller = useSeller();

    const partnerPurchaseRules = partnerXolaPartner.purchaseRuleLink
        ? getPurchaseRulesFromPurchaseRuleLink(partnerXolaPartner.purchaseRuleLink)
        : [];
    const selectedProducts = getProductSelectionFromPurchaseRules(partnerPurchaseRules);

    const params: any = {
        authorizePartnerExperiences: true,
    };

    if (selectedProducts.selected && selectedProducts.selected.length > 0) {
        params["id[in]"] = selectedProducts.selected.join(",");
    }

    const { data: partnerExperiences, isLoading: isLoadingPartnerExperiences } = useExperiences({
        partnerExperiences: true,
        partnerSeller: partnerXolaPartner.seller.id,
    });

    // we store the partner sellers data in value, which we cannot pass a query string since the preference.data don't have type in API models
    // so, we fetch by key and context and filterout the seller prefernce.
    const { data: preferences, isLoading: isLoadingPreference } = useAutoFetchCollection<SellerPartnerPreference>(
        "preferences",
        {
            key: PreferenceKey.XOLA_SELLER_PARTNER,
            context: PreferenceContext.SELLER,
        },
    );

    const partnerExperienceIds = partnerExperiences?.map((experience) => experience.id) ?? [];
    const filteredPrefernces = preferences
        ?.filter((preference) => preference.value.partnerSeller === partner.partnerSeller.id)
        .sort((a, b) => (b.id ?? "").localeCompare(a.id ?? ""));

    const partnerExperiencePreference = filteredPrefernces?.[0];

    const { isLoading: isSavingPreference, mutate: savePreference } = useMutation(
        "preferences",
        async (preference: SellerPartnerPreference) => {
            const { data: result } = preference.id
                ? await api.patch<SellerPartnerPreference>(`/preferences/${preference.id}`, preference)
                : await api.post<SellerPartnerPreference>("/preferences", preference);
            flash.success("Operator terms saved successfully");
            invalidate("preferences", result.id);
        },
    );

    const { isLoading: isDeletingPreference, mutate: deletePartnerPreference } = useMutation(
        "preferences",
        async (preference: SellerPartnerPreference) => {
            await api.delete(`/preferences/${preference.id}`);
            invalidate("preferences", preference.id);
        },
    );

    const handleSubmit = async (values: PartnerTermsFormValues) => {
        if (
            values.partnerResells &&
            values.selectedExperiences.length === partnerExperienceIds.length &&
            partnerExperiencePreference
        ) {
            // All Experiences are selected and we are reselling products, so we can delete the preference
            return deletePartnerPreference(partnerExperiencePreference);
        }

        const preference: SellerPartnerPreference = {
            key: PreferenceKey.XOLA_SELLER_PARTNER,
            seller: { id: seller.id },
            context: PreferenceContext.SELLER,
            value: {
                partnerSeller: partner.partnerSeller.id,
                selectedProducts: values.selectedExperiences,
            },
        };

        savePreference(preference);
    };

    if (isLoadingPartnerExperiences || isLoadingPreference) {
        return <Loader />;
    }

    return (
        <XolaFormik
            enableReinitialize
            validateOnChange={false}
            initialValues={getInitialValues(partner, partnerExperienceIds, partnerExperiencePreference)}
            onSubmit={handleSubmit}
        >
            {({ handleSubmit, handleReset, values, dirty, setFieldValue }) => {
                return (
                    <ContentLayout isWide classNames={{ root: "!mb-0 pb-0" }}>
                        <div className="space-y-4 divide-y divide-gray-light pl-6">
                            <div className="flex space-x-14 py-12" data-testid="agent-purchase-settings-form-products">
                                <div
                                    className="flex max-w-[320px] flex-col"
                                    data-testid="agent-purchase-settings-form-products"
                                >
                                    <h3>Operator Terms & Products</h3>
                                    <p className="text-gray-darker">
                                        Enable and select the operator’s products you would like to resell according to
                                        their terms.
                                    </p>
                                </div>
                                <div className="w-1/2 pl-16">
                                    <div className="flex flex-col space-y-2">
                                        <span className="font-bold">Permissions</span>
                                        <div className="flex items-center">
                                            <Switch
                                                isChecked={values.partnerResells}
                                                disabled={[XolaPartnerConnectionType.RESELLER].includes(
                                                    partner.connectionType,
                                                )}
                                                onChange={(value: boolean) => setFieldValue("partnerResells", value)}
                                            />
                                            <span className="pl-1">
                                                I want to sell {partner?.partnerSeller.name} products
                                            </span>
                                        </div>
                                    </div>
                                    {values.partnerResells && partnerXolaPartner && (
                                        <PartnerTermsList
                                            partnerXolaPartner={partnerXolaPartner}
                                            partnerExperiences={partnerExperiences}
                                            values={values}
                                            onChange={(values: PartnerTermsFormValues) =>
                                                setFieldValue("selectedExperiences", values.selectedExperiences)
                                            }
                                        />
                                    )}
                                </div>
                            </div>
                        </div>
                        <ContentLayout.Footer>
                            <SubmitButton
                                type="submit"
                                isLoading={isSavingPreference || isDeletingPreference || isLoadingPartnerExperiences}
                                disabled={!dirty}
                                onClick={handleSubmit}
                            >
                                Save
                            </SubmitButton>

                            <SecondaryButton type="reset" onClick={handleReset}>
                                Cancel
                            </SecondaryButton>
                        </ContentLayout.Footer>
                    </ContentLayout>
                );
            }}
        </XolaFormik>
    );
};

// Returns InitialValues for the form
const getInitialValues = (
    partner: XolaPartner,
    partnerExperienceIds: ID[],
    partnerExperiencePreference?: SellerPartnerPreference,
): PartnerTermsFormValues => {
    // Current seller resells, only if connection type is either of those
    const sellerResells =
        partner.connectionType === XolaPartnerConnectionType.SUPPLIER_RESELLER ||
        partner.connectionType === XolaPartnerConnectionType.SUPPLIER;

    if (!sellerResells) {
        return {
            partnerResells: false,
            selectedExperiences: [],
        };
    }

    let selectedExperiences = partnerExperienceIds;
    if (partnerExperiencePreference && partnerExperiencePreference.value?.selectedProducts?.length > 0) {
        // No Preference exists, so we will
        selectedExperiences = partnerExperiencePreference.value.selectedProducts;
    }

    // const isSelectedAllExperiences = !partnerExperiencePreference ||
    const isResellingTurnedOff =
        partnerExperiencePreference && partnerExperiencePreference.value?.selectedProducts?.length === 0;

    return {
        partnerResells: !isResellingTurnedOff,
        selectedExperiences: selectedExperiences,
    };
};

const PartnerTermsList = ({
    partnerXolaPartner,
    partnerExperiences,
    values,
    onChange,
}: {
    partnerXolaPartner: XolaPartner;
    partnerExperiences: Experience[];
    values: PartnerTermsFormValues;
    onChange: (values: PartnerTermsFormValues) => void;
}) => {
    const partnerTermsTemplate = partnerXolaPartner.purchaseRuleLink!;
    const partnerPurchaseRules: PurchaseRule[] =
        compact(partnerTermsTemplate?.purchaseRules?.map(({ purchaseRule }) => purchaseRule as PurchaseRule)) ?? [];

    const partnerTermsValues = useFormValues(partnerExperiences, partnerPurchaseRules, partnerTermsTemplate);

    return partnerTermsValues ? (
        <div className="space-y-1 pt-6">
            <span className="font-bold">Products & Terms</span>
            <PartnerProductTermsTable
                partnerExperiences={partnerExperiences}
                termsTemplate={partnerTermsValues}
                values={values}
                onChange={onChange}
            />
        </div>
    ) : null;
};

interface PartnerProductTermsTableProps {
    partnerExperiences: Experience[];
    termsTemplate: TermsTemplate;
    values: PartnerTermsFormValues;
    onChange: (values: PartnerTermsFormValues) => void;
}

export const PartnerProductTermsTable = ({
    partnerExperiences,
    termsTemplate,
    values,
    onChange,
}: PartnerProductTermsTableProps) => {
    let productIds = termsTemplate.products.selected;
    if (
        !termsTemplate.products.all &&
        termsTemplate.products.selected?.length &&
        termsTemplate.termsType === TermsType.ADVANCED
    ) {
        productIds = termsTemplate.products.selected ?? [];
    } else {
        productIds = partnerExperiences.map((experience) => experience.id);
    }

    const products = compact(
        productIds.map((productId) => partnerExperiences.find((experience) => experience.id === productId)),
    );

    const handleChange = (productId: string, value: boolean) => {
        const updatedValue = produce(values, (draft) => {
            const selectedExperiences = [...draft.selectedExperiences];
            if (value) {
                selectedExperiences.push(productId);
            } else {
                const index = selectedExperiences.indexOf(productId);
                selectedExperiences.splice(index, 1);
            }

            draft.selectedExperiences = uniq(selectedExperiences);
        });

        onChange(updatedValue);
    };

    return (
        <div>
            {products.length > 0 &&
                products.map((product: Experience) => (
                    <ProductTermsItem
                        key={product.id}
                        product={product}
                        termsTemplate={termsTemplate}
                        values={values}
                        onChange={handleChange}
                    />
                ))}
        </div>
    );
};

interface ProductTermsItemProps {
    product: Experience;
    termsTemplate: TermsTemplate;
    values: PartnerTermsFormValues;
    onChange: (productId: ID, enabled: boolean) => void;
}

export const ProductTermsItem = ({ product, termsTemplate, values, onChange }: ProductTermsItemProps) => {
    const partnerDemographicTerm = getPartnerDemographicTerm(termsTemplate.partnerDemographicTerms, product);
    const partnerDemographicPrices = getPartnerDemographicPrices(product, termsTemplate, partnerDemographicTerm);
    const isEnabled = values.partnerResells && values.selectedExperiences.includes(product.id);

    return product ? (
        <>
            <div className="mb-2 flex items-center">
                <div className="flex w-full items-center justify-between">
                    <div className="flex items-center">
                        <ProductImage
                            product={{ id: product.id, name: product.name, object: product.object }}
                            size="small"
                        />
                        <Label className="no-translate ml-2">{product.name}</Label>
                    </div>
                    <div className="flex items-center space-x-2">
                        <Switch isChecked={isEnabled} onChange={(value: boolean) => onChange(product.id, value)} />
                        <span>Enable</span>
                    </div>
                </div>
            </div>
            {isEnabled ? <DemographicTermTable partnerDemographicPrices={partnerDemographicPrices} /> : null}
        </>
    ) : null;
};

export const getPartnerDemographicTerm = (partnerDemographicTerms: PartnerDemographicTerm[], product: Experience) => {
    return partnerDemographicTerms?.find(
        (partnerDemographicTerm: PartnerDemographicTerm) => partnerDemographicTerm.product.id === product.id,
    );
};

const getPartnerDemographicPrices = (
    product: Experience,
    termsTemplate: TermsTemplate,
    partnerDemographicTerm?: PartnerDemographicTerm,
): PartnerDemographicPrice[] => {
    const partnerDemographicPrices: PartnerDemographicPrice[] = [];

    const demographicCatalogItems: CatalogItem[] = getDemographicCatalog(product);
    if (partnerDemographicTerm?.partnerDemographicPrices?.length) {
        for (const catalogItem of demographicCatalogItems) {
            const agentDemographicPrice = partnerDemographicTerm.partnerDemographicPrices.find(
                (agentDemographicPrice: PartnerDemographicPrice) =>
                    agentDemographicPrice.template.id === catalogItem.template?.id,
            );
            partnerDemographicPrices.push(getInitialPartnerDemographicPrices(catalogItem, agentDemographicPrice));
        }
    } else {
        // initialise demographic Prices form value and update, if no partnerDemographicPrices exists
        for (const catalogItem of demographicCatalogItems) {
            const template = { id: catalogItem.template?.id ?? "" };
            const defaultDemographicPrice: PartnerDemographicPrice = {
                template,
                discount: convertTermTemplateFeeToDemographicPrice(template, catalogItem, termsTemplate.discount),
                commission: convertTermTemplateFeeToDemographicPrice(template, catalogItem, termsTemplate.commission),
            };
            partnerDemographicPrices.push(getInitialPartnerDemographicPrices(catalogItem, defaultDemographicPrice));
        }
    }

    return partnerDemographicPrices;
};

const convertTermTemplateFeeToDemographicPrice = (
    template: { id: ID },
    catalogItem: CatalogItem,
    templateFee: TermsTemplateFee,
): DemographicPrice => {
    let operation = AdjustmentOperation.MARKUP;
    if (templateFee.amount && templateFee.amount < 0) {
        operation = AdjustmentOperation.MARKDOWN;
    }

    return {
        adjustmentType: (templateFee.algorithm as unknown as AdjustmentType) ?? AdjustmentType.ABSOLUTE,
        adjustmentAmount: Math.abs(templateFee.amount ?? 0),
        adjustmentOperation: operation,
        template,
        catalogItem: { ...catalogItem },
    };
};

export const useFormValues = (
    partnerExperiences: Experience[],
    purchaseRules: PurchaseRule[],
    termsTemplate?: PurchaseRuleLink,
): TermsTemplate | null => {
    const termsType = getTermsType(termsTemplate);
    const demographicTemplates = getLineItemTemplatesFromProducts(partnerExperiences);
    const discountPurchaseRule = useFindPurchaseRule(LineItemType.AFFILIATE_DISCOUNT, termsType, purchaseRules);
    const commissionPurchaseRule = useFindPurchaseRule(LineItemType.AFFILIATE_COMMISSION, termsType, purchaseRules);

    const discountPurchaseRuleAdvance = getPurchaseRulesByTag(LineItemType.AFFILIATE_DISCOUNT, purchaseRules);
    const selectedProducts = getProductSelectionFromPurchaseRules(discountPurchaseRuleAdvance);

    if (!discountPurchaseRule || !commissionPurchaseRule) {
        return null;
    }

    if (termsType === TermsType.ADVANCED && !discountPurchaseRuleAdvance) {
        return null;
    }

    const value = {
        id: termsTemplate?.id,
        title: termsTemplate?.name ?? "",
        products: termsType === TermsType.ADVANCED ? selectedProducts : getProductSelection(discountPurchaseRule),
        discount: getPurchaseRuleInputValues(discountPurchaseRule, termsTemplate, demographicTemplates),
        commission: getPurchaseRuleInputValues(commissionPurchaseRule, termsTemplate, demographicTemplates),
        termsType: termsType,
        partnerDemographicTerms: generatePartnerDemographicTerms(purchaseRules, partnerExperiences),
        productTermExceptions: generateProductTermExecptions(purchaseRules, termsTemplate),
    };

    return value;
};

const useFindPurchaseRule = (
    lineItemType: LineItemType.AFFILIATE_COMMISSION | LineItemType.AFFILIATE_DISCOUNT,
    termsType: TermsType,
    purchaseRules: PurchaseRule[],
): PurchaseRule | null => {
    if (termsType === TermsType.ADVANCED) {
        return initializeTermsTemplatePurchaseRule(lineItemType);
    }

    const termsTemplatePurchaseRule = purchaseRules.find((purchaseRule) => {
        const isGivenTemplateRule = purchaseRule.tags?.some((tag) => tag.id === lineItemType);
        const isOverridedRule = purchaseRule.tags?.some((tag) => tag.id === TagId.AFFILIATE_OVERRIDE);
        return isGivenTemplateRule && !isOverridedRule;
    });

    return termsTemplatePurchaseRule ?? initializeTermsTemplatePurchaseRule(lineItemType);
};

export const initializeTermsTemplatePurchaseRule = (lineItemType: LineItemType): PurchaseRule => {
    const typeText = lineItemType === LineItemType.AFFILIATE_DISCOUNT ? "Discount" : "Commission";

    return {
        name: `${typeText} for ${typeText}`,
        actions: [
            {
                template: { id: "" },
                object: PurchaseRuleActionObject.INSERT_LINE_ITEM_ACTION,
                shortcut: {
                    type: PurchaseRuleShortcutType.ABSOLUTE_PER_UNIT,
                    amount: null as any,
                    options: { templateCodes: ["demographic_subtotal"] },
                },
            },
        ],
        tags: [{ id: "Affiliate" }, { id: lineItemType }],
    };
};

/**
 * Convert terms template purchase rule to form values
 */
export const getPurchaseRuleInputValues = (
    purchaseRule: PurchaseRule,
    termsTemplate?: PurchaseRuleLink,
    demographicTemplates?: LineItemTemplate[],
): TermsTemplateFee => {
    return {
        amount: termsTemplate ? getAmount(purchaseRule) : 0,
        algorithm: getAlgorithm(purchaseRule),
        scope: getScope(purchaseRule),
        demographics: getDemographicSelection(purchaseRule, demographicTemplates),
        includeAddOns: getIncludesAddOns(purchaseRule),
        includeCustomLineItems: getIncludeCustomLineItems(purchaseRule),
    };
};

export const getAmount = (termsTemplate: PurchaseRule): number => {
    const [action] = termsTemplate.actions;
    if (action.shortcut) {
        return Number(action.shortcut.amount);
    }

    return 0;
};

/**
 * This function returns line Item Templates from products. this is needed for partner experiences
 * since we don't have access to GET line item templates, so we are generating the lineItem templates from catalog
 * as we will have all the information.
 */
const getLineItemTemplatesFromProducts = (experiences: Experience[]) => {
    const lineItemTemplates: LineItemTemplate[] = [];
    for (const experience of experiences) {
        const demographicCatalog = getDemographicCatalog(experience);
        for (const catalogItem of demographicCatalog) {
            if (catalogItem.template) {
                const lineItemTemplate: LineItemTemplate = {
                    id: catalogItem.template.id,
                    name: catalogItem.template.name ?? catalogItem.name,
                    code: LineItemCode.DEMOGRAPHIC,
                    object: LineItemTemplateObject.DEFAULT,
                    type: LineItemType.DEMOGRAPHIC,
                    destination: LineItemDestination.SELLER,
                    visible: true,
                    visibility: LineItemVisibility.ALL,
                    system: false,
                    includeInAmount: true,
                    seller: { id: experience.seller.id },
                    createdAt: dayjs(),
                    updatedAt: dayjs(),
                    createdBy: { id: "" },
                };
                const index = lineItemTemplates.findIndex((template) => template.id === lineItemTemplate.id);
                if (index < 0) {
                    lineItemTemplates.push(lineItemTemplate);
                }
            }
        }
    }

    return lineItemTemplates;
};

// TODO: split up this file to smaller components
